import { useState, useEffect, useCallback } from 'react';
import { galleryContent } from '../data/mock';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export const GallerySection = () => {
  const [ref, isVisible] = useScrollReveal();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const { t } = useLanguage();

  const openLightbox = (index) => {
    setActiveIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    document.body.style.overflow = '';
  }, []);

  const goNext = useCallback((e) => {
    if (e) e.stopPropagation();
    setActiveIndex((prev) => (prev + 1) % galleryContent.images.length);
  }, []);

  const goPrev = useCallback((e) => {
    if (e) e.stopPropagation();
    setActiveIndex(
      (prev) => (prev - 1 + galleryContent.images.length) % galleryContent.images.length
    );
  }, []);

  // Keyboard navigation
  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxOpen, closeLightbox, goNext, goPrev]);

  return (
    <>
      <section id="galerie" className="relative py-28 bg-[#0A0A0A]" ref={ref}>
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#C9A84C]/20 to-transparent" />

        <div className="max-w-7xl mx-auto px-6">
          {/* Section Header */}
          <div className="mb-12">
            <div
              className={`flex items-center gap-3 mb-4 transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <div className="h-[1px] w-8 bg-[#C9A84C]" />
              <span className="text-[#C9A84C] text-sm font-medium tracking-[0.2em] uppercase">
                {t.gallery.tagline}
              </span>
            </div>
            <h2
              className={`text-4xl sm:text-5xl font-bold text-[#F5F0E8] font-heading transition-all duration-700 delay-100 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              {t.gallery.headline}
            </h2>
          </div>

          {/* Gallery Grid - 3 columns, featured first image */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {galleryContent.images.map((image, i) => {
              // First image (Modrić selfie) is featured large
              const isFeatured = i === 0;
              return (
                <div
                  key={i}
                  className={`group relative overflow-hidden rounded-xl border border-[#1F1F1F] cursor-pointer hover:border-[#C9A84C]/40 transition-all duration-500 ${
                    isFeatured ? 'col-span-2 row-span-2' : ''
                  } ${
                    isVisible
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-8'
                  }`}
                  style={{ transitionDelay: `${150 + i * 80}ms` }}
                  onClick={() => openLightbox(i)}
                >
                  <img
                    src={image.src}
                    alt={image.alt}
                    className={`w-full ${isFeatured ? 'h-full' : 'aspect-square'} object-cover group-hover:scale-105 transition-transform duration-700`}
                  />
                  {/* Dark overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  {/* Caption on hover */}
                  <div className="absolute bottom-0 left-0 right-0 p-3 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">
                    <p className="text-[#F5F0E8] text-xs font-medium truncate">
                      {image.alt}
                    </p>
                  </div>
                  {/* Gold corner accent */}
                  <div className="absolute top-0 right-0 w-10 h-[2px] bg-[#C9A84C]/0 group-hover:bg-[#C9A84C]/60 transition-all duration-500" />
                  <div className="absolute top-0 right-0 w-[2px] h-10 bg-[#C9A84C]/0 group-hover:bg-[#C9A84C]/60 transition-all duration-500" />
                  <div className="absolute bottom-0 left-0 w-10 h-[2px] bg-[#C9A84C]/0 group-hover:bg-[#C9A84C]/60 transition-all duration-500" />
                  <div className="absolute bottom-0 left-0 w-[2px] h-10 bg-[#C9A84C]/0 group-hover:bg-[#C9A84C]/60 transition-all duration-500" />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[100] bg-[#0A0A0A]/95 backdrop-blur-xl flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 w-11 h-11 rounded-full bg-[#1A1A1A]/80 border border-[#C9A84C]/20 flex items-center justify-center text-[#F5F0E8] hover:bg-[#C9A84C]/20 hover:border-[#C9A84C]/50 transition-all duration-300 z-10"
          >
            <X size={20} />
          </button>

          {/* Previous */}
          <button
            onClick={goPrev}
            className="absolute left-4 sm:left-8 w-11 h-11 rounded-full bg-[#1A1A1A]/80 border border-[#C9A84C]/20 flex items-center justify-center text-[#F5F0E8] hover:bg-[#C9A84C]/20 hover:border-[#C9A84C]/50 transition-all duration-300 z-10"
          >
            <ChevronLeft size={20} />
          </button>

          {/* Image */}
          <div
            className="max-w-5xl max-h-[85vh] px-16"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={galleryContent.images[activeIndex].src}
              alt={galleryContent.images[activeIndex].alt}
              className="max-w-full max-h-[78vh] object-contain rounded-xl border border-[#C9A84C]/10"
            />
            <div className="flex items-center justify-between mt-4 px-1">
              <p className="text-[#F5F0E8] text-sm font-medium">
                {galleryContent.images[activeIndex].alt}
              </p>
              <div className="flex items-center gap-2">
                <span className="text-[#C9A84C] text-sm font-mono">
                  {String(activeIndex + 1).padStart(2, '0')}
                </span>
                <span className="text-[#555] text-sm">/</span>
                <span className="text-[#555] text-sm font-mono">
                  {String(galleryContent.images.length).padStart(2, '0')}
                </span>
              </div>
            </div>
            {/* Thumbnail strip */}
            <div className="flex gap-2 mt-4 overflow-x-auto pb-2 justify-center">
              {galleryContent.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  className={`flex-shrink-0 w-12 h-12 rounded-md overflow-hidden border-2 transition-all duration-300 ${
                    i === activeIndex
                      ? 'border-[#C9A84C] opacity-100'
                      : 'border-transparent opacity-40 hover:opacity-70'
                  }`}
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Next */}
          <button
            onClick={goNext}
            className="absolute right-4 sm:right-8 w-11 h-11 rounded-full bg-[#1A1A1A]/80 border border-[#C9A84C]/20 flex items-center justify-center text-[#F5F0E8] hover:bg-[#C9A84C]/20 hover:border-[#C9A84C]/50 transition-all duration-300 z-10"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      )}
    </>
  );
};
